import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Beacon from '@99designs/apps/spa/images/auth/error-art/Beacon_ByDusanKlepic.jpg';
import CharacterDesign from '@99designs/apps/spa/images/auth/error-art/CharacterDesign_ByJG10G.jpg';
import IsometricCity from '@99designs/apps/spa/images/auth/error-art/IsometricCity_ByLittleFox.jpg';
import TheBlackCat from '@99designs/apps/spa/images/auth/error-art/TheBlackCat-ByPANG3STU.jpg';
import UndyingLove from '@99designs/apps/spa/images/auth/error-art/UndyingLove_Bathi.jpg';
import UniqueDesigns from '@99designs/apps/spa/images/auth/error-art/UniqueDesigns_ByBRTHR-ED.jpg';
import { sanitizeUrlWithOrigin } from '@99designs/browser';
import { Box, Button, Heading, Paragraph, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { FormattedHTMLMessage, formatMessage } from '@99designs/frontend-components/i18n';
import { ContentBody } from '@99designs/spa-common';
import { useSite } from '@99designs/spa-common';
import { Page } from '@99designs/spa-common';
import { DesignerImage } from '../common/Designer/DesignerImage';

const ErrorContainerBox = styled(Box)`
    display: grid;
    grid-template-columns: 1fr 1fr;

    ${mediaQuery.between('small', 'medium')} {
        display: flex;
        flex-direction: column-reverse;
        align-items: center;
    }
`;

const ErrorArtworkBox = styled(Box)`
    position: relative;
    grid-row: span 2;
    max-width: 454px;

    ${mediaQuery.between('small', 'medium')} {
        max-width: 300px;
    }
`;

const ErrorInfoBox = styled(Box)`
    display: flex;
    flex-direction: column;
    max-width: 420px;
    margin-top: ${spaces['6bu']};

    ${mediaQuery.between('small', 'medium')} {
        margin-top: ${spaces['2bu']};
        max-width: 349px;
    }
`;

const ParagraphStyled = styled(Paragraph)`
    padding-bottom: ${spaces['1.5bu']};

    ${mediaQuery.between('small', 'medium')} {
        width: 100%;
    }
`;

const ButtonStyled = styled(Box)`
    display: flex;
    flex-direction: row;

    ${mediaQuery.between('small', 'medium')} {
        flex-direction: column-reverse;
    }
`;

const GoBackButton = styled(Button)`
    ${mediaQuery.minimum('normal')} {
        margin-right: ${spaces['0.75bu']};
    }
`;

const SecondaryButton = styled(Button)`
    ${mediaQuery.between('small', 'medium')} {
        margin-bottom: ${spaces['0.75bu']};
    }
`;

function FullscreenSVAError() {
    const site = useSite();
    const [searchParams] = useSearchParams();
    const errorCode = searchParams.get('error') || undefined;
    const errorCodeKey = errorCode as ErrorCodeKeys;
    const errorContent =
        getContentByErrorCode(errorCodeKey) || getContentByErrorCode('unknown-error');
    const target =
        sanitizeUrlWithOrigin(searchParams.get('target') || '/', 'https://' + site.getDomain()) ||
        '/';
    const referer =
        sanitizeUrlWithOrigin(searchParams.get('referer') || '/', 'https://' + site.getDomain()) ||
        '/';
    const secondaryButtonLink =
        errorContent.secondaryButtonType && errorContent.secondaryButtonType === 'help'
            ? '/help' //TODO: add correct help link
            : target;

    return (
        <Page title={errorContent.heading}>
            <Helmet
                bodyAttributes={{
                    class: 'max--desktop transparent-site-header no-footer',
                }}
            />
            <ContentBody maxWidth="desktop">
                <ErrorContainerBox marginTop={['none', '3bu']}>
                    <ErrorInfoBox>
                        <Stack space={'1.5bu'}>
                            <Heading accessibilityLevel={1} appearanceLevel={2}>
                                {errorContent.heading}
                            </Heading>
                            <ParagraphStyled size="large">
                                <FormattedHTMLMessage message={errorContent.message} />
                            </ParagraphStyled>
                        </Stack>

                        <ButtonStyled>
                            <GoBackButton
                                href={referer}
                                appearance="tertiary"
                                size="medium"
                                width={['fill', 'auto', 'auto']}
                            >
                                {__('Go Back')}
                            </GoBackButton>

                            {errorContent.secondaryButtonText && (
                                <SecondaryButton
                                    href={secondaryButtonLink}
                                    size="medium"
                                    appearance="primary"
                                    openInNewTab={errorContent.secondaryButtonType === 'help'}
                                    data-testid="secondary-button"
                                    width={['fill', 'auto', 'auto']}
                                >
                                    {errorContent.secondaryButtonText}
                                </SecondaryButton>
                            )}
                        </ButtonStyled>
                    </ErrorInfoBox>

                    <ErrorArtworkBox margin="auto" maxWidth="60vw">
                        <DesignerImage
                            image={errorContent.image}
                            imgAlt={formatMessage(__('by {displayName}'), {
                                displayName: errorContent.imageAttribution,
                            })}
                            name={errorContent.imageAttribution}
                            link={errorContent.designerLink}
                            borderRadius="100%"
                        />
                    </ErrorArtworkBox>
                </ErrorContainerBox>
            </ContentBody>
        </Page>
    );
}

export default FullscreenSVAError;

export type ErrorCodeKeys =
    | 'oauth-failed'
    | 'oauth-account-creation'
    | 'incorrect-login-method-use-okta'
    | 'geo'
    | 'unknown-error'
    | 'cant-login'
    | 'unhandled-error';

type ErrorContentByCodeType = {
    [key in ErrorCodeKeys]: {
        message: string;
        heading: string;
        image: string;
        imageAttribution: string;
        secondaryButtonText?: string;
        secondaryButtonType?: 'help' | 'try_again';
        designerLink: string;
    };
};

function getContentByErrorCode(errCode: ErrorCodeKeys) {
    const contentByErrorCode: ErrorContentByCodeType = {
        'oauth-failed': {
            heading: __('Could not connect'),
            message: __('Please try logging in again.'),
            image: Beacon,
            imageAttribution: 'DusanKlepic',
            secondaryButtonText: __('Try again'),
            secondaryButtonType: 'try_again',
            designerLink: '/profiles/dusanklepic',
        },
        'oauth-account-creation': {
            heading: __('Your account could not be created'),
            message: __(
                "Hmm, this doesn't look right! Please contact customer support to continue setting up your account."
            ),
            image: TheBlackCat,
            imageAttribution: 'PANG3STU',
            secondaryButtonText: __('Contact Customer Support'),
            secondaryButtonType: 'help',
            designerLink: '/profiles/PANG3STU',
        },
        'incorrect-login-method-use-okta': {
            heading: __('Please log in with Okta'),
            message: __('Admin users need to log in using Okta. <br/> You got this!'),
            image: UndyingLove,
            imageAttribution: 'BATHI',
            designerLink: '/profiles/BATHI',
        },
        geo: {
            heading: __('Registration failed'),
            message: __('Registrations aren’t available in your country.'),
            image: IsometricCity,
            imageAttribution: 'LittleFox',
            designerLink: '/profiles/LittleFox',
        },
        'unknown-error': {
            heading: __('An unknown error has occurred'),
            message: __('Please try logging in again.'),
            image: UniqueDesigns,
            imageAttribution: 'BRTHR-ED',
            secondaryButtonText: __('Try again'),
            secondaryButtonType: 'try_again',
            designerLink: '/profiles/1127882',
        },
        'cant-login': {
            heading: __('Login failed'),
            message: __('Sorry, you can’t log in at this time.'),
            image: CharacterDesign,
            imageAttribution: 'JG10G',
            designerLink: '/profiles/JG10G',
        },
        'unhandled-error': {
            heading: __('An unknown error has occurred'),
            message: __(
                "Hmm, this doesn't look right! Please contact customer support to continue."
            ),
            image: UniqueDesigns,
            imageAttribution: 'BRTHR-ED',
            secondaryButtonText: __('Contact Customer Support'),
            secondaryButtonType: 'help',
            designerLink: '/profiles/1127882',
        },
    };

    return contentByErrorCode[errCode];
}
